import { UserRecord } from '@tassat/payments-definitions';
import { ActionTypes } from '@tassat/ui-definitions';
import { Dispatch, SetStateAction } from 'react';

import { AdminUserFormProps, IErrorValues, IFormValues } from './types';
import {
    firmNameAndWalletTextValidation,
    textFieldCharacterWhitelist,
    validateDescriptionInput,
    validateEmailFormatting,
    validateFullNameUniqueness,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable,
    validatePcgUserMappedPcgNumbers,
    validateRequired,
    validateSsoUserIdFormatting,
    validateUsernameFormatting
} from '../helpers/validationHelpers';

export const validateAdminUserForm = async (values: IFormValues, opts: {
    actionType: ActionTypes;
    initialValues: IFormValues;
    isAdmin: boolean;
    firmId: string;
    features?: AdminUserFormProps['features'];
    users: {
        [key: string]: UserRecord;
    };
    debouncedUsernameValidation: (value: string) => Promise<string | null>;
    isUsernameValidated: boolean;
    setIsUsernameValidated: Dispatch<SetStateAction<boolean>>;
}) => {
    const {
        actionType,
        initialValues,
        isAdmin,
        firmId,
        users,
        features,
        debouncedUsernameValidation,
        isUsernameValidated,
        setIsUsernameValidated
    } = opts;

    const errors: IErrorValues = {} as IErrorValues;

    const firstName = values.FirstName;
    const lastName = values.LastName;
    const username = values.Username;
    const notes = values.Notes;

    const fullName = (`${firstName} ${lastName}`);
    const initalFullName = `${initialValues.FirstName} ${initialValues.LastName}`;

    const emailFormattingError = validateEmailFormatting(values.Email, true);
    const emailExcelError = validateNoExcelFormulaStartingCharacters(values.Email);
    const emailNonPrintableError = validateNoNonPrintable(values.Email);

    const nameError = validateFullNameUniqueness(fullName, users, firmId);

    const firstNameWhiteList = textFieldCharacterWhitelist(firstName);
    const lastNameWhiteList = textFieldCharacterWhitelist(lastName);
    const usernameWhiteList = textFieldCharacterWhitelist(username);
    const notesNameWhiteList = textFieldCharacterWhitelist(notes);

    const firstNameRequiredError = validateRequired(values.FirstName);
    const firstNameGarbageCharsError = validateDescriptionInput(values.FirstName, 'First Name');
    const firstNameExcelError = validateNoExcelFormulaStartingCharacters(values.FirstName);
    const firstNameNonPrintableError = validateNoNonPrintable(values.FirstName);
    const firstNameValidateNoSpecialCharactersError = firmNameAndWalletTextValidation(values.FirstName);

    const lastNameRequiredError = validateRequired(values.LastName);
    const lastNameGarbageCharsError = validateDescriptionInput(values.LastName, 'Last Name');
    const lastNameExcelError = validateNoExcelFormulaStartingCharacters(values.LastName);
    const lastNameNonPrintableError = validateNoNonPrintable(values.LastName);
    const lastNameValidateNoSpecialCharactersError = firmNameAndWalletTextValidation(values.LastName);

    const roleRequiredError = isAdmin && validateRequired(values.AdminRoleId?.value);

    const userPcgValidation = validatePcgUserMappedPcgNumbers(values);

    const notesGarbageCharsError = validateDescriptionInput(values.Notes, 'Notes');
    const notesExcelError = validateNoExcelFormulaStartingCharacters(values.Notes);
    const notesNonPrintableError = validateNoNonPrintable(values.Notes);
    const phoneNonPrintableError = validateNoNonPrintable(values.Phone);

    const usernameRequiredError = validateRequired(values.Username);
    // TODO: BTEN-580: Use the server endpoint to validate username uniqueness.
    // const UsernameUniqueError = validateUsernameUniqueness(values.Username, users, initialValues.Username);
    let usernameFormattingError = features?.SSO ?
        validateSsoUserIdFormatting(values.Username, true)
        : validateUsernameFormatting(values.Username, true);

    if (features?.loginType === 'Auth0' && !features?.SSO) {
        usernameFormattingError = null;
    }

    if (firstNameWhiteList){
        errors.FirstName = firstNameWhiteList;
    }
    if (lastNameWhiteList){
        errors.LastName = lastNameWhiteList;
    }
    if (usernameWhiteList){
        errors.Username = usernameWhiteList;
    }
    if (notesNameWhiteList){
        errors.Notes = notesNameWhiteList;
    }

    if (firstNameRequiredError) {
        errors.FirstName = firstNameRequiredError;
    } else if (firstNameGarbageCharsError) {
        errors.FirstName = firstNameGarbageCharsError;
    } else if (firstNameExcelError) {
        errors.FirstName = firstNameExcelError;
    } else if (firstNameNonPrintableError) {
        errors.FirstName = firstNameNonPrintableError;
    } else if (firstNameValidateNoSpecialCharactersError) {
        errors.FirstName = firstNameValidateNoSpecialCharactersError;
    }

    if (lastNameRequiredError) {
        errors.LastName = lastNameRequiredError;
    } else if (lastNameGarbageCharsError) {
        errors.LastName = lastNameGarbageCharsError;
    } else if (lastNameExcelError) {
        errors.LastName = lastNameExcelError;
    } else if (lastNameNonPrintableError) {
        errors.LastName = lastNameNonPrintableError;
    } else if (lastNameValidateNoSpecialCharactersError) {
        errors.LastName = lastNameValidateNoSpecialCharactersError;
    }

    if (nameError && (actionType === ActionTypes.add ||
        (actionType === ActionTypes.edit && fullName !== initalFullName))) {
        errors.FirstName = nameError;
        errors.LastName = nameError;
    }

    if (roleRequiredError) {
        errors.AdminRoleId = roleRequiredError;
    }

    if (emailFormattingError) {
        errors.Email = emailFormattingError;
    } else if (emailExcelError) {
        errors.Email = emailExcelError;
    } else if (emailNonPrintableError) {
        errors.Email = emailNonPrintableError;
    }

    if (notesGarbageCharsError) {
        errors.Notes = notesGarbageCharsError;
    } else if (notesExcelError) {
        errors.Notes = notesExcelError;
    } else if (notesNonPrintableError) {
        errors.Notes = notesNonPrintableError;
    }

    if (userPcgValidation) {
        errors.PCGMapping = userPcgValidation;
    }

    if (phoneNonPrintableError) {
        errors.Phone = phoneNonPrintableError;
    }

    if (usernameRequiredError) {
        errors.Username = usernameRequiredError;
    }

    if (usernameFormattingError) {
        errors.Username = usernameFormattingError;
    }

    if (!usernameFormattingError) {
        if (features?.loginType !== 'Auth0' || features?.SSO) {
            if (actionType === ActionTypes.add || actionType === ActionTypes.sso_validate){
                if (!isUsernameValidated) {
                    const error = await debouncedUsernameValidation(values.Username);
                    if (error) {
                        errors.Username = error;
                    } else {
                        setIsUsernameValidated(true);
                    }
                }
            }
        }
    }

    return errors;
};